import Fieldset from 'components/_UI/Fieldset'
import Modal from 'components/_UI/Modal'
import React, { useEffect, useState } from 'react'
import { Column } from 'styled/alignment/Column'
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import styled from 'styled-components'
import { AlertCircle, Ticket } from 'lucide-react'
import Button from 'components/_UI/Button'
import breakpoints from 'theme/breakpoints'
import Switch from 'components/_UI/Switch'
import fonts from 'theme/fonts'
import { useForm } from 'react-hook-form'
import fieldValidations from 'helpers/fieldValidations'
import colors from 'theme/colors'
import { useEvent } from 'context/events/Event'
import promotionalCodesApi from 'api/requests/promotionalCodes'
import { FullyCentered } from 'styled/alignment/Center'
import Loader from 'components/_UI/Loader'
import buttonText from 'texts/buttons/buttonText'
import ticketsApi from 'api/requests/tickets'
import { StyledLabel } from 'styled/UI/StyledForm'
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';

/**
 * react-select desenhado como o `Fieldset layout='squired'`: mesma altura, mesmo
 * raio, mesma borda e o mesmo anel roxo de foco. Antes os selects eram
 * visivelmente de outra biblioteca no meio do formulário.
 */
const selectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: '42px',
        marginTop: '6px',
        borderRadius: '8px',
        borderWidth: '1.5px',
        borderColor: state.isFocused ? panelAccent(colors.occasionalPurple) : '#D4D4D4',
        boxShadow: state.isFocused ? `0 0 0 3px ${panelAccentAlpha(colors.occasionalPurple, '1A')}` : 'none',
        fontSize: fonts.sizes.default,
        '&:hover': { borderColor: state.isFocused ? panelAccent(colors.occasionalPurple) : colors.nightLight },
    }),
    valueContainer: (base) => ({ ...base, padding: '2px 12px' }),
    placeholder: (base) => ({ ...base, color: colors.nightLight }),
    singleValue: (base) => ({ ...base, color: colors.nightDark }),
    input: (base) => ({ ...base, color: colors.nightDark }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base) => ({ ...base, color: colors.nightLight, padding: '8px' }),
    menu: (base) => ({
        ...base,
        borderRadius: '10px',
        overflow: 'hidden',
        boxShadow: '0 8px 24px rgba(16, 24, 40, 0.12)',
        zIndex: 30,
    }),
    option: (base, state) => ({
        ...base,
        fontSize: fonts.sizes.default,
        cursor: 'pointer',
        backgroundColor: state.isSelected
            ? panelAccent(colors.secondaryPurple)
            : state.isFocused ? '#F7EFFF' : 'transparent',
        color: state.isSelected ? colors.neutralLight : colors.nightDark,
    }),
    groupHeading: (base) => ({
        ...base,
        fontSize: '10px',
        letterSpacing: '0.6px',
        color: colors.night,
    }),
    multiValue: (base) => ({
        ...base,
        backgroundColor: '#F7EFFF',
        border: '1px solid #DFC8F8',
        borderRadius: '6px',
    }),
    multiValueLabel: (base) => ({ ...base, color: '#6D14CC', fontSize: '12px', fontWeight: 600 }),
    multiValueRemove: (base) => ({
        ...base,
        color: '#6D14CC',
        ':hover': { backgroundColor: '#EBDCFB', color: '#4E0E93' },
    }),
};

const applicationSummary = {
    ticket: 'em ingressos',
    product: 'em produtos',
    both: 'em ingressos e produtos',
};

export default function CreatePromotionalCode({ visible, isEdit, toast, onClose, onSuccess }) {
    const { event } = useEvent();

    const [discountTypes, setDiscountTypes] = useState([]);
    const [selectedApplicationType, setSelectedApplicationType] = useState();
    const [selectedDiscountType, setSelectedDiscountType] = useState();
    const [autocompleteSelected, setAutocompleteSelected] = useState(null);
    const [active, setActive] = useState(true);
    const [loading, setLoading] = useState(false);
    const [discountTypeLoading, setDiscountTypeLoading] = useState(false);
    const [autocomplete, setAutocomplete] = useState(false);
    const [loaderAutocomplete, setLoaderAutocomplete] = useState(false);
    const [loaderItemsToApplication, setLoaderItemsToApplication] = useState(false);
    const [appliedIn, setAppliedIn] = useState(false);
    const [selectedAppliedItems, setSelectedAppliedItems] = useState([]);

    const [error, setError] = useState(false);
    const [codeError, setCodeError] = useState(false)
    const [applicationTypeError, setApplicationTypeError] = useState(false)
    const [discountTypeError, setDiscountTypeError] = useState(false)

    const {
        register,
        handleSubmit,
        reset,
        watch,
        formState: { errors },
    } = useForm({
        defaultValues: {
            quantity: '',
            code: '',
            value: ''
        }
    });

    useEffect(() => {
        Promise.all([
            getCouponTypes(),
            getOldersCoupons()
        ])

        if (isEdit && discountTypes) {
            const { value, max_uses, is_active, coupon_type_id, code, discount_type } = isEdit;

            setAutocompleteSelected(isEdit)

            reset({
                quantity: max_uses,
                value,
                code
            })

            fillApplicationType(discount_type)
            fillDiscountType(coupon_type_id);
            setActive(Boolean(is_active));

            if (discount_type) {
                const selectedType = applicationTypes.find(el => el.value === discount_type);
                setSelectedApplicationType(selectedType);

                getItemsToApply(selectedType).then((data) => {
                    if (Array.isArray(isEdit.batch_ids) && isEdit.batch_ids.length && data?.length) {
                        const mappedItems = mapBatchIdsToLabels(isEdit.batch_ids, data);
                        setSelectedAppliedItems(mappedItems);
                    }
                });
            }

        }
    }, [isEdit, discountTypes])

    const applicationTypes = [
        { label: 'Ingresso', value: 'ticket' },
        { label: 'Produtos', value: 'product' },
        { label: 'Todos', value: 'both' },
    ];

    function mapBatchIdsToLabels(batchIds, data) {
        return batchIds.map(id => {
            const item = data.find(i => i.id === id);
            return {
                value: id,
                label: item?.name || `Item ${id}`
            };
        });
    }

    function fillApplicationType(item) {
        const selected = applicationTypes.find(el => el.value === item);
        setSelectedApplicationType(selected);
    }

    function fillDiscountType(item) {
        if (discountTypes) {
            const selected = discountTypes.find(el => el.id === item);
            setSelectedDiscountType(selected);
        }
    }

    async function getCouponTypes() {
        if (discountTypes.length) {
            return
        }

        setDiscountTypeLoading(true);

        try {
            const { data } = await promotionalCodesApi.getTypes(event?.slug);

            const mappedDiscountTypes = data.map(item => ({
                label: renderDiscountTypeText(item?.symbol),
                value: item.symbol,
                id: item.id
            }));

            setDiscountTypes(mappedDiscountTypes);
        } catch (error) {
            console.log('error: ', error);
        } finally {
            setDiscountTypeLoading(false);
        }
    }

    async function getOldersCoupons() {
        setLoaderAutocomplete(true);
        try {
            const { data } = await promotionalCodesApi.getDistincts();

            setAutocomplete(Object.values(data));
        } catch (error) {
            console.log('error: ', error);
        } finally {
            setLoaderAutocomplete(false);
        }
    }

    function renderDiscountTypeText(symbol) {
        if (symbol == '%') {
            return 'Porcentagem (%)'
        }

        if (symbol == 'R$') {
            return 'Desconto fixo (R$)'
        }
    }

    async function handlePayload({ code, quantity, value }) {
        const payload = {
            code,
            value,
            coupon_type_id: selectedDiscountType?.id,
            event_id: event?.id,
            discount_type: selectedApplicationType?.value,
            max_uses: quantity,
            is_active: active,
            batch_ids: selectedAppliedItems?.length ? selectedAppliedItems.map(item => Number(item.value)) : [],
        };

        setLoading(true);

        !isEdit ? createCode(payload) : updateCode(payload);

    }

    function selectsError(payload) {
        if (payload?.code && payload?.discount_type && payload?.coupon_type_id) {
            setCodeError(false);
            setApplicationTypeError(false);
            setDiscountTypeError(false);

            return false
        }

        if (!payload?.code) {
            setCodeError(true);
        }

        if (!payload?.discount_type) {
            setApplicationTypeError(true);
        }

        if (!payload?.coupon_type_id) {
            setDiscountTypeError(true);
        }

        return true
    }

    async function createCode(payload) {
        setError(false);

        if (selectsError(payload)) {
            setLoading(false);
            return
        }

        try {
            const { data } = await promotionalCodesApi.create(payload);
            onSuccess(data);
            toast.success('Código promocional criado com sucesso!', {
                position: 'top-center'
            })
        } catch ({ response }) {
            if (response.status == '500') {
                setError(response?.data?.message);
            }
        } finally {
            setLoading(false);
        }
    }

    function onSelectAutocomplete(item) {
        const { is_active, coupon_type_id, code, discount_type, max_uses, value } = item;

        if (typeof (item) == 'string') {
            setAutocompleteSelected({ code: item });
            reset({ code: item })
            return
        }

        setAutocompleteSelected(item);

        reset({
            quantity: max_uses,
            code,
            value
        })

        fillApplicationType(discount_type)
        fillDiscountType(coupon_type_id);
        setActive(Boolean(is_active));
    }

    async function updateCode(payload) {
        setError(false);

        if (selectsError(payload)) {
            setLoading(false);
            return
        }

        try {
            const { data } = await promotionalCodesApi.update(isEdit?.id, payload);
            onSuccess(data);
            toast.success('Código promocional atualizado com sucesso!', {
                position: 'top-center'
            })
        } catch ({ response }) {
            if (response.status == '500') {
                setError(response?.data?.message);
            }
        } finally {
            setLoading(false);
        }
    }

    async function getItemsToApply(item) {
        setLoaderItemsToApplication(true);

        try {
            const { data } = await ticketsApi.getAllItems(event?.slug, {
                type: item?.value,
            });
            setAppliedIn(data); // manter isso

            // atualizar os labels se já tiver seleção feita
            // if (Array.isArray(selectedAppliedItems) && selectedAppliedItems.length) {
            //     const updatedSelected = selectedAppliedItems.map(selected => {
            //         const match = data.find(i => i.id === selected.value);
            //         return {
            //             ...selected,
            //             label: match?.name || selected.label
            //         }
            //     });

            //     setSelectedAppliedItems(updatedSelected);
            // } else {
            //     setSelectedAppliedItems([]);
            // }

            return data; // ✅ retorna os itens aplicáveis
        } catch (error) {
            console.log('Erro ao carregar aplicáveis', error);
            return [];
        } finally {
            setLoaderItemsToApplication(false);
        }
    }


    function onSelectApplicationType(item) {
        setSelectedApplicationType(item);
        setSelectedAppliedItems([]); // ← Limpa antes
        getItemsToApply(item);
    }
    

    const appliedInItems = () => {
        if (!appliedIn?.length) return [];

        const tickets = appliedIn
            .filter(item => item.type === 'ticket')
            .map(el => ({ label: el.name, value: el.id }));

        const products = appliedIn
            .filter(item => item.type === 'product')
            .map(el => ({ label: el.name, value: el.id }));

        const groups = [];

        groups.push({
            label: 'Todos',
            options: [{ label: 'Selecionar todos', value: 'all' }]
        });

        if (tickets.length) {
            groups.push({
                label: 'Ingressos',
                options: tickets
            });
        }

        if (products.length) {
            groups.push({
                label: 'Produtos',
                options: products
            });
        }

        return groups;
    }


    /* Resumo em linguagem natural do que está sendo montado. O produtor escolhe
       "tipo de aplicação" e "tipo de desconto" em selects separados e só
       descobria o resultado depois de salvar. */
    const preview = (() => {
        const code = watch('code') || autocompleteSelected?.code;
        const value = watch('value');
        const quantity = watch('quantity');
        const symbol = selectedDiscountType?.value;

        if (!code || !value || !symbol) return null;

        const discount = symbol === '%' ? `${value}%` : `R$ ${value}`;
        const where = applicationSummary[selectedApplicationType?.value] || '';
        const limit = quantity ? ` · até ${quantity} ${Number(quantity) === 1 ? 'uso' : 'usos'}` : '';

        return `${code} dá ${discount} de desconto ${where}${limit}`.replace(/\s+/g, ' ').trim();
    })();

    return (
        <Modal isOpen={visible} onClose={onClose} size='medium'>
            <ModalHeader>
                <ModalTitle>{isEdit ? 'Editar código promocional' : 'Novo código promocional'}</ModalTitle>
                <ModalSubtitle>
                    O desconto vale para o pedido inteiro, uma vez por compra.
                </ModalSubtitle>
            </ModalHeader>

            <Form onSubmit={handleSubmit(handlePayload)}>
                <Field>
                    {
                        loaderAutocomplete ? (
                            <FullyCentered height='60px'>
                                <Loader />
                            </FullyCentered>
                        ) : (
                            autocomplete.length && !isEdit ? (
                                <>
                                    <StyledLabel>Código</StyledLabel>
                                    <CreatableSelect
                                        isClearable={true}
                                        styles={selectStyles}
                                        formatCreateLabel={(inputValue) => inputValue}
                                        placeholder='Digite um código novo'
                                        value={{ label: autocompleteSelected?.code, value: autocompleteSelected }}
                                        options={autocomplete.map(el => ({ label: el.code, value: el }))}
                                        onChange={e => onSelectAutocomplete(e?.value || e)}
                                    />
                                    <Hint>Digite um código novo ou reaproveite um que você já usou em outro evento.</Hint>
                                    {codeError && <FieldError><AlertCircle size={13} />O campo código é obrigatório</FieldError>}
                                </>
                            ) : (
                                <>
                                    <Fieldset
                                        label='Código'
                                        layout='squired'
                                        placeholder='Ex.: AMIGOS10'
                                        register={register}
                                        name='code'
                                        validations={fieldValidations.name('código')}
                                    />
                                    {/* A dica sai de cena quando há erro: duas linhas de apoio
                                        embaixo do mesmo campo empurram o formulário e competem. */}
                                    {errors.code?.message
                                        ? <FieldError><AlertCircle size={13} />{errors.code?.message}</FieldError>
                                        : <Hint>É o que a pessoa vai digitar no checkout.</Hint>}
                                </>
                            )
                        )
                    }
                </Field>

                {/* Desconto antes de onde ele vale: é a decisão que o produtor já
                    tem na cabeça quando abre o formulário. */}
                <FieldGrid>
                    <Field>
                        <StyledLabel>Tipo de desconto</StyledLabel>
                        <Select
                            placeholder='Porcentagem ou valor'
                            styles={selectStyles}
                            value={selectedDiscountType}
                            options={discountTypes}
                            isDisabled={discountTypeLoading}
                            onChange={e => setSelectedDiscountType(e)}
                        />
                        {discountTypeError && <FieldError><AlertCircle size={13} />Escolha o tipo de desconto</FieldError>}
                    </Field>

                    <Field>
                        {/* O sufixo evita a dúvida de sempre: "10" aqui são 10% ou R$ 10? */}
                        <AffixWrapper $affix={selectedDiscountType?.value}>
                            <Fieldset
                                label='Valor do desconto'
                                layout='squired'
                                placeholder={selectedDiscountType?.value === 'R$' ? '25' : '10'}
                                register={register}
                                name='value'
                                validations={fieldValidations.number('valor')}
                            />
                            {selectedDiscountType?.value && <Affix>{selectedDiscountType.value}</Affix>}
                        </AffixWrapper>
                        {errors.value?.message && <FieldError><AlertCircle size={13} />{errors.value?.message}</FieldError>}
                    </Field>
                </FieldGrid>

                <FieldGrid>
                    <Field>
                        <StyledLabel>Vale para</StyledLabel>
                        <Select
                            placeholder='Ingressos e/ou produtos'
                            styles={selectStyles}
                            value={selectedApplicationType}
                            options={applicationTypes}
                            onChange={e => onSelectApplicationType(e)}
                        />
                        {applicationTypeError && <FieldError><AlertCircle size={13} />Escolha onde o cupom vale</FieldError>}
                    </Field>

                    <Field>
                        <Fieldset
                            label='Limite de usos'
                            layout='squired'
                            placeholder='100'
                            register={register}
                            name='quantity'
                            validations={fieldValidations.number('quantidade')}
                        />
                        {errors.quantity?.message
                            ? <FieldError><AlertCircle size={13} />{errors?.quantity?.message}</FieldError>
                            : <Hint>Quantas vezes o cupom pode ser resgatado no total.</Hint>}
                    </Field>
                </FieldGrid>

                {
                    loaderItemsToApplication ? (
                        <FullyCentered height='60px'>
                            <Loader />
                        </FullyCentered>
                    ) : (
                        !!appliedIn.length && (
                            <Field $spaced>
                                <StyledLabel>Itens específicos</StyledLabel>
                                <Select
                                    isMulti
                                    styles={selectStyles}
                                    placeholder='Todos os itens do tipo escolhido'
                                    value={selectedAppliedItems}
                                    options={appliedInItems()}
                                    onChange={(selectedOptions) => {
                                        const selectedAll = selectedOptions?.some(opt => opt.value === 'all');

                                        if (selectedAll) {
                                            const allOptions = appliedIn
                                                .map(item => ({
                                                    label: item.name,
                                                    value: item.id
                                                }));

                                            setSelectedAppliedItems(allOptions);
                                        } else {
                                            setSelectedAppliedItems(selectedOptions);
                                        }
                                    }}
                                />
                                <Hint>Deixe vazio para valer em tudo que você marcou acima.</Hint>
                            </Field>
                        )
                    )
                }

                <SwitchCard>
                    <Switch
                        label='Código ativo'
                        labelSize={fonts.sizes.default}
                        onChange={(e) => setActive(e)}
                        checked={active}
                    />
                    <SwitchHint>
                        {active
                            ? 'Pode ser resgatado no checkout assim que você salvar.'
                            : 'Fica salvo, mas ninguém consegue usar até você reativar.'}
                    </SwitchHint>
                </SwitchCard>

                {preview && (
                    <Preview>
                        <Ticket size={15} strokeWidth={2.5} aria-hidden='true' />
                        <span>{preview}</span>
                    </Preview>
                )}

                {error && (
                    <FormError role='alert'>
                        <AlertCircle size={14} />
                        {error}
                    </FormError>
                )}

                {
                    loading ? (
                        <FullyCentered height='58px'>
                            <Loader />
                        </FullyCentered>
                    ) : (
                        <ButtonsContainer>
                            <Button variant={'light'} category={'roundedBorded'} onClick={onClose}>
                                {buttonText.cancel}
                            </Button>

                            <Button variant={'featured'} category={'roundedBorded'} type={'submit'}>
                                {!isEdit ? buttonText.create : buttonText.update} código
                            </Button>
                        </ButtonsContainer>
                    )
                }
            </Form>
        </Modal>
    )
}

/* ─────────────────────────────  Estilos  ───────────────────────────── */

const ModalHeader = styled.header`
    text-align: left;
    padding-right: 28px;
`;

const ModalTitle = styled.h2`
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const ModalSubtitle = styled.p`
    margin: 4px 0 0;
    font-size: 12px;
    color: ${colors.night};
`;

const Form = styled.form`
    width: 100%;
    margin-top: 18px;
    text-align: left;
`;

const Field = styled.div`
    width: 100%;
    text-align: left;

    /* Espaçamento explícito: uma regra de irmão adjacente também alcançaria os
       campos lado a lado da grade e desalinharia a segunda coluna em 14px. */
    ${({ $spaced }) => $spaced && `margin-top: 14px;`}
`;

/* Duas colunas a partir de 620px: abaixo disso os campos ficavam com metade da
   largura em telas de celular, porque o breakpoint antigo era 375px. */
const FieldGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 14px;
    margin-top: 14px;

    @media screen and (min-width: ${breakpoints.sm}) {
        grid-template-columns: 1fr 1fr;
    }
`;

const Hint = styled.p`
    margin: 6px 0 0;
    font-size: 11px;
    line-height: 1.4;
    color: ${colors.night};
`;

const FieldError = styled.p`
    display: flex;
    align-items: center;
    gap: 5px;
    margin: 6px 0 0;
    font-size: 11px;
    font-weight: 600;
    color: ${colors.dangerDark};
`;

const AffixWrapper = styled.div`
    position: relative;

    input {
        padding-right: ${({ $affix }) => ($affix ? '46px' : '14px')};
    }
`;

/* Ancorado no rodapé do Fieldset (que reserva 8px de padding-bottom) e centrado
   na altura do input, para o símbolo ficar na mesma linha do número digitado. */
const Affix = styled.span`
    position: absolute;
    right: 14px;
    bottom: 8px;
    height: 42px;
    display: flex;
    align-items: center;
    font-size: ${fonts.sizes.default};
    font-weight: 600;
    color: ${colors.night};
    pointer-events: none;
`;

const SwitchCard = styled.div`
    margin-top: 16px;
    padding: 12px 14px;
    border-radius: 10px;
    border: 1px solid #e2e4ea;
    background: ${colors.neutralBackground};
`;

const SwitchHint = styled.p`
    margin: 4px 0 0;
    font-size: 11px;
    color: ${colors.night};
`;

const Preview = styled.p`
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 14px 0 0;
    padding: 10px 12px;
    border-radius: 10px;
    background: #f7efff;
    border: 1px solid #dfc8f8;
    color: #6d14cc;
    font-size: 12px;
    font-weight: 600;
    line-height: 1.4;

    svg {
        flex-shrink: 0;
    }
`;

const FormError = styled.p`
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 14px 0 0;
    padding: 10px 12px;
    border-radius: 10px;
    background: #fdf0f1;
    border: 1px solid #f4d3d6;
    color: ${colors.dangerDark};
    font-size: 12px;
    font-weight: 600;
`;

const ButtonsContainer = styled(Column)`
    margin-top: 20px;

    @media screen and (min-width: ${breakpoints.sm}) {
        flex-direction: row;
        justify-content: flex-end;

        & > button {
            width: auto;
            min-width: 140px;
            padding-left: 22px;
            padding-right: 22px;
        }

        button:nth-child(2) {
            margin-left: 10px;
        }
    }
`;

